import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { HospitalService } from './../../../../core/services/hospital.service';
import { PatientService } from './../../../../core/services/patient.service';
import { Router } from '@angular/router';
import { RENDER_BATCH } from './../../../../shared/directives/render-more.directive';

@Component({
  selector: 'app-archive-list',
  templateUrl: './archive-list.component.html',
  styleUrls: ['./archive-list.component.scss']
})
export class ArchiveListComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  /** Rows rendered so far; the rest render as the end of the list nears the screen. */
  shown = RENDER_BATCH;
  readonly renderBatch = RENDER_BATCH;
  @Input() filterValue: string = '';
  @Input() sortBy: 'surname' | 'firstName' | 'patientNumber' = 'surname';
  @Input() sortDirection: 'asc' | 'desc' = 'asc';
  archives: any[] = [];

  // Initially set to sample values; these will be replaced when hospitals$ emits.
  statusOptions: any[] = [
    { id: 'opd', name: 'Outpatients' },
    { id: 'inpatient', name: 'Inpatients' },
    { id: 'invite', name: 'Invites' }
  ];

  // This will store the sections to display – one per status option that has matching patients.
  patientStatusOptions: any[] = [];

  filteredArchives: any[] = [];
  // This will be an object where keys are status ids and values are sorted arrays of patients.
  filteredArchivesByStatus: { [key: string]: any[] } = {};

  public expandedSection: string | null = null;

  constructor(
    private patientService: PatientService,
    private router: Router,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    // Subscribe to patients
    this.patientService.archives$
      .pipe(takeUntil(this.destroy$))
      .subscribe(archives => {
        this.archives = archives;
        this.applyFilter(); // This sets filteredArchives and updates the grouping
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['filterValue'] || changes['sortBy'] || changes['sortDirection']) {
      this.applyFilter();
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  applyFilter() {
    this.shown = RENDER_BATCH;
    if (!this.archives) {
      return;
    }

    // If no filter, show all archives
    if (this.filterValue.trim() === '') {
      this.filteredArchives = this.archives;
    } else {
      const filterTerms = this.filterValue.toLowerCase().split(' ');
      this.filteredArchives = _.filter(this.archives, patient => {
        const icdCodesMatch = patient.icdCodes?.some((icdCode: any) =>
          filterTerms.every(term =>
            _.includes((icdCode.code || '').toLowerCase(), term) ||
            _.includes((icdCode.description || '').toLowerCase(), term)
          )
        );

        const patientMatch = filterTerms.every(term =>
          _.includes(patient.name.toLowerCase(), term) ||
          _.includes(patient.surname.toLowerCase(), term) ||
          _.includes(patient.patientNumber?.toLowerCase(), term) ||
          _.includes(patient.diagnosis?.toLowerCase(), term)
        );

        return icdCodesMatch || patientMatch;
      });
    }

    // After filtering, group the archives by their status.
    this.groupFilteredArchivesByStatus();
  }

  groupFilteredArchivesByStatus() {
    // Group archives by the 'status' field (which should match a statusOption id)
    const archivesGrouped = _.groupBy(this.filteredArchives, 'status');
    const prop = this.sortBy === 'firstName' ? 'name' : this.sortBy;
    const secondaryProp = prop === 'surname' ? 'name' : 'surname';
    const dir = this.sortDirection;
    const norm = (v: any) => (v == null ? '' : String(v)).trim().toLowerCase();

    // Sort each group by current sort settings (case-insensitive, trimmed)
    _.forOwn(archivesGrouped, (patients, status) => {
      archivesGrouped[status] = _.orderBy(
        patients,
        [(p: any) => norm(p[prop]), (p: any) => norm(p[secondaryProp])],
        [dir, dir]
      );
    });

    this.filteredArchivesByStatus = archivesGrouped;
    this.updateArchiveStatusList();
  }

  updateArchiveStatusList() {
    if (!this.statusOptions) {
      return;
    }

    // Create sections only for statuses that have matching archives
    const desiredOrder = ['opd', 'inpatient', 'invite'];
    this.patientStatusOptions = _.orderBy(
      _.filter(this.statusOptions, statusOption =>
        _.has(this.filteredArchivesByStatus, statusOption.id)
      ),
      statusOption => desiredOrder.indexOf(statusOption.id)
    ).map(statusOption => ({
      id: statusOption.id,
      name: statusOption.name,
      archives: this.filteredArchivesByStatus[statusOption.id] || []
    }));

    // Expand the first section by default (if none is already expanded)
    if (this.patientStatusOptions.length > 0 && this.expandedSection === null) {
      this.expandedSection = this.patientStatusOptions[0].id;
    }
  }

  restorePatientFile(event: MouseEvent, patientId: string) {
    event.preventDefault();
    event.stopPropagation();
    this.patientService.updatePatient(patientId, { active: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.showToastMessage('Patient file restored successfully');
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error restoring the patient file. Please try again.');
      });
  }

  navigateToPatientFile(event: MouseEvent, patientId: string) {
    event.preventDefault();
    event.stopPropagation();
    this.patientService.getPatientById(patientId)
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }

  toggleExpanded(statusId: string) {
    this.shown = RENDER_BATCH;
    this.expandedSection = this.expandedSection === statusId ? null : statusId;
  }
}
